import { defineStore } from 'pinia';
import { Branch } from '@/types';
import RequestsUtils from '@/helpers/RequestsUtils';
import { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';
import { computed, ref } from 'vue';

export const useBranchesStore = defineStore('branches', () => {
  // Data
  const _list = ref([] as Branch[]);
  const _loading = ref(false);
  const _listPromiseResolve = ref(null);
  const _listPromiseReject = ref(null);
  const _listPromise = ref(null);

  const branchIdFromRoute = ref(null as Branch['id']);
  const selectedBranchId = ref(null as Branch['id']);
  const defaultBranch = ref('prod');

  // Computed
  const selectedBranch = computed((): Branch => {
    return _list.value.find((branch: Branch) => {
      return branch.id === selectedBranchId.value;
    });
  });
  const branchesCounter = computed((): number => {
    return _.size(_list.value);
  });

  // Methods
  function getBranches(): Promise<Branch[]> {
    if (_loading.value) {
      return _listPromise.value;
    } else {
      return new Promise((resolve) => {
        resolve(_list.value);
      });
    }
  }

  async function loadBranches() {
    if (_loading.value) {
      return _listPromise.value;
    }
    _loading.value = true;
    _listPromise.value = new Promise((resolve, reject) => {
      _listPromiseResolve.value = resolve;
      _listPromiseReject.value = reject;
    }) as Promise<Branch[]>;
    RequestsUtils.sendRequest({
      methodName: 'GET',
      url: 'conf/configs',
    })
      .then((response: AxiosResponse) => {
        if (response?.data?.items) {
          _list.value = response.data.items;
          setSelectedBranch(branchIdFromRoute.value);
        }
        _loading.value = false;
        _listPromiseResolve.value(_list.value);
      })
      .catch((err: AxiosError) => {
        console.log('Error while attempting to load branches');
        console.log(err);
        _loading.value = false;
        _listPromiseReject.value(err);
      });
    return _listPromise.value;
  }

  function setSelectedBranch(id?: Branch['id']) {
    const branches = _list.value;
    let newId = defaultBranch.value;
    if (id) {
      const branch = branches.find((branch: Branch) => {
        return branch.id === id;
      });
      if (branch) {
        newId = id;
      }
    }
    selectedBranchId.value = newId;
  }

  return {
    selectedBranchId,
    selectedBranch,
    branchesCounter,
    defaultBranch,
    branchIdFromRoute,
    getBranches,
    loadBranches,
    setSelectedBranch,
  };
});
